import { Case, ClientCompat, LandTransporter } from '../types';

/**
 * 指定された月の案件をフィルタリング
 */
export const filterCasesByMonth = (cases: Case[], year: number, month: number): Case[] => {
  return cases.filter(case_ => {
    const pickupDate = new Date(case_.pickupDate);
    return pickupDate.getFullYear() === year && pickupDate.getMonth() === month - 1;
  });
};

/**
 * 完了案件のみをフィルタリング
 */
export const filterCompletedCases = (cases: Case[]): Case[] => {
  return cases.filter(case_ => case_.status === 'completed');
};

/**
 * 月間売上合計を計算
 */
export const calculateMonthlySales = (cases: Case[]): number => {
  return cases.reduce((total, case_) => total + case_.price, 0);
};

/**
 * 顧客別売上を計算
 */
export const calculateSalesByClient = (
  cases: Case[],
  getClientById: (id: string) => ClientCompat | undefined
): Array<{
  clientId: string;
  clientName: string;
  totalSales: number;
  caseCount: number;
}> => {
  const salesByClient = new Map<string, { totalSales: number; caseCount: number }>();

  cases.forEach(case_ => {
    const existing = salesByClient.get(case_.billingClientId) || { totalSales: 0, caseCount: 0 };
    salesByClient.set(case_.billingClientId, {
      totalSales: existing.totalSales + case_.price,
      caseCount: existing.caseCount + 1,
    });
  });

  return Array.from(salesByClient.entries())
    .map(([clientId, data]) => {
      const client = getClientById(clientId);
      return {
        clientId,
        clientName: client?.companyName || '不明',
        totalSales: data.totalSales,
        caseCount: data.caseCount,
      };
    })
    .sort((a, b) => b.totalSales - a.totalSales);
};

/**
 * 陸送員別稼働実績を計算
 */
export const calculateDriverPerformance = (
  cases: Case[],
  drivers: LandTransporter[]
): Array<{
  driverId: string;
  driverName: string;
  totalCases: number;
  completedCases: number;
  totalSales: number;
  completionRate: number;
}> => {
  const performanceByDriver = new Map<
    string,
    {
      totalCases: number;
      completedCases: number;
      totalSales: number;
    }
  >();

  cases.forEach(case_ => {
    if (case_.assignedLandTransporterId) {
      const existing = performanceByDriver.get(case_.assignedLandTransporterId) || {
        totalCases: 0,
        completedCases: 0,
        totalSales: 0,
      };

      performanceByDriver.set(case_.assignedLandTransporterId, {
        totalCases: existing.totalCases + 1,
        completedCases: existing.completedCases + (case_.status === 'completed' ? 1 : 0),
        totalSales: existing.totalSales + (case_.status === 'completed' ? case_.price : 0),
      });
    }
  });

  return Array.from(performanceByDriver.entries())
    .map(([driverId, data]) => {
      const driver = drivers.find(d => d.id === driverId);
      return {
        driverId,
        driverName: driver?.name || '不明',
        totalCases: data.totalCases,
        completedCases: data.completedCases,
        totalSales: data.totalSales,
        completionRate: data.totalCases > 0 ? (data.completedCases / data.totalCases) * 100 : 0,
      };
    })
    .sort((a, b) => b.totalSales - a.totalSales);
};

/**
 * 請求書データを生成
 */
export const generateBillingData = (
  cases: Case[],
  clientId: string,
  year: number,
  month: number
) => {
  const billingCases = cases.filter(
    case_ =>
      case_.billingClientId === clientId &&
      case_.status === 'completed' &&
      filterCasesByMonth([case_], year, month).length > 0
  );

  const totalAmount = billingCases.reduce((sum, case_) => sum + case_.price, 0);

  return {
    cases: billingCases,
    totalAmount,
    caseCount: billingCases.length,
  };
};

/**
 * 月名を取得
 */
export const getMonthName = (month: number): string => {
  const months = [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ];
  return months[month - 1] || '';
};

/**
 * ステータスの日本語表示
 */
export const getStatusText = (status: Case['status']): string => {
  switch (status) {
    case 'provisional':
      return '仮受';
    case 'confirmed':
      return '本受・未配車';
    case 'assigned':
      return '配車済み';
    case 'picked_up':
      return '引き取り済み';
    case 'in_transit':
      return '輸送中';
    case 'delivered':
      return '搬入済み';
    case 'reported':
      return '報告済み';
    case 'completed':
      return '完了';
    default:
      return '不明';
  }
};
